import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, X, Filter, Globe, Pause, Play, Trash2, ArrowLeft, ShieldCheck, Users } from "lucide-react";
import api from "../lib/api";
import { Spinner } from "../components/common";
import { useAuth } from "../context/AuthContext";

/* Spec §7.5 — Trusted Travel Circle management page. Owner curates a list of
   people who automatically receive read-only access to matching future trips.
   Never grants edit/passport/cost — just view or helper access. */

const PRESET_LABELS = {
  view:   { label: "Can View",  desc: "Sees the itinerary only. No costs, no PNRs, no documents." },
  helper: { label: "Can Edit",  desc: "Can edit shared tasks and itinerary items. Does NOT count as a traveler." },
  custom: { label: "Custom",    desc: "Advanced — you'll set each capability manually." },
};

const TRIP_TYPES = [
  { id: "any",           label: "All my trips" },
  { id: "international", label: "International trips only" },
  { id: "family",        label: "Family trips only" },
  { id: "leisure",       label: "Leisure trips only" },
  { id: "business",      label: "Business trips only" },
];

export default function TrustedCircle() {
  const { user } = useAuth() || {};
  const [items, setItems] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const load = () =>
    api.get("/trusted-circle/members").then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));

  useEffect(() => { load(); }, []);

  const patch = async (mid, patch) => {
    try {
      await api.patch(`/trusted-circle/members/${mid}`, patch);
      toast.success("Updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update");
    }
  };

  const revoke = async (mid, email) => {
    if (!window.confirm(`Remove ${email} from your Trusted Circle? They lose access to all shared trips immediately.`)) return;
    try {
      await api.delete(`/trusted-circle/members/${mid}`);
      toast.success("Removed from your Inner Circle");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove");
    }
  };

  if (!user) {
    return (
      <div className="max-w-lg mx-auto p-8 text-center">
        <ShieldCheck className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <div className="font-serif font-800 text-2xl text-slate-800">Sign in to manage your Inner Circle</div>
        <Link to="/login" className="inline-block mt-4 bg-blue-600 text-white px-5 py-2.5 rounded-full font-700">Sign in</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-5 py-8" data-testid="trusted-circle-page">
      <Link to="/travel-hub" className="inline-flex items-center gap-1 text-blue-700 text-sm hover:underline mb-3">
        <ArrowLeft className="w-4 h-4" /> Back to My Travel Hub
      </Link>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-amber-600 flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> Spec §7.5 · Inner Circle
          </div>
          <h1 className="font-serif font-800 text-4xl text-slate-900 mt-1">Inner Circle</h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            People in your Inner Circle can see your detailed trip plans and are automatically notified when you add new trips that match your filters. They never see confirmation numbers, costs, loyalty numbers or documents unless you explicitly grant Edit access. You can exclude any trip individually.
          </p>
        </div>
        <button onClick={() => setAddOpen(true)}
          data-testid="trusted-circle-add"
          className="shrink-0 inline-flex items-center gap-2 rounded-full bg-blue-600 hover:brightness-110 text-white text-sm font-700 px-5 py-2.5">
          <UserPlus className="w-4 h-4" /> Add to Inner Circle
        </button>
      </div>

      <div className="mt-8">
        {items === null ? <Spinner /> : items.length === 0 ? (
          <div className="rounded-3xl border-2 border-dashed border-slate-200 p-10 text-center">
            <ShieldCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <div className="font-serif font-800 text-xl text-slate-800">Your circle is empty</div>
            <div className="text-sm text-slate-500 mt-1 max-w-md mx-auto">Add a parent, sibling, close friend or emergency contact who should see your future trips at a glance.</div>
          </div>
        ) : (
          <ul className="space-y-3">
            {items.map((m) => (
              <li key={m.id} data-testid={`trusted-member-${m.id}`}
                className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-4 shadow-sm">
                <div className="w-11 h-11 rounded-full bg-blue-100 text-blue-700 grid place-items-center font-700 shrink-0">
                  {(m.member_name || m.member_email || "?")[0].toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-serif font-800 text-lg text-slate-900 truncate">{m.member_name || m.member_email}</div>
                  <div className="text-xs text-slate-500 truncate">{m.member_email}</div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px]">
                    <span className="bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5 font-700">{PRESET_LABELS[m.preset]?.label || m.preset}</span>
                    <span className="bg-slate-50 text-slate-700 border border-slate-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                      <Filter className="w-3 h-3" /> {TRIP_TYPES.find((t) => t.id === m.trip_type_filter)?.label || m.trip_type_filter}
                    </span>
                    {m.destination_filter?.length > 0 && (
                      <span className="bg-slate-50 text-slate-700 border border-slate-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                        <Globe className="w-3 h-3" /> {m.destination_filter.join(", ")}
                      </span>
                    )}
                    {m.status === "paused" && <span className="bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5 font-700">Paused</span>}
                    {(m.excluded_trip_ids?.length || 0) > 0 && <span className="bg-slate-50 text-slate-600 rounded-full px-2 py-0.5">{m.excluded_trip_ids.length} excluded</span>}
                  </div>
                </div>
                <button
                  onClick={() => patch(m.id, { paused: m.status !== "paused" })}
                  title={m.status === "paused" ? "Resume auto-share" : "Pause auto-share"}
                  data-testid={`trusted-toggle-${m.id}`}
                  className="w-9 h-9 rounded-full grid place-items-center text-slate-500 hover:bg-slate-100">
                  {m.status === "paused" ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => revoke(m.id, m.member_email)}
                  title="Remove"
                  data-testid={`trusted-remove-${m.id}`}
                  className="w-9 h-9 rounded-full grid place-items-center text-rose-600 hover:bg-rose-50">
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {addOpen && <AddModal onClose={() => setAddOpen(false)} onAdded={() => { setAddOpen(false); load(); }} />}
    </div>
  );
}

function AddModal({ onClose, onAdded }) {
  const { user } = useAuth() || {};
  const [emails, setEmails] = useState([]);      // chip list of recipient emails
  const [emailDraft, setEmailDraft] = useState("");
  const [preset, setPreset] = useState("helper"); // "Can Edit" checked by default per screenshot
  const [busy, setBusy] = useState(false);

  const ownerName = user?.name || (user?.email || "").split("@")[0] || "This traveler";
  const messagePreview = `${ownerName} wants to add you to their Inner Circle. As part of ${ownerName}'s Inner Circle, you can see their detailed trip plans and automatically get notified when they add new trips.`;

  const addChip = () => {
    const e = (emailDraft || "").trim().toLowerCase();
    if (!e) return;
    if (!/^\S+@\S+\.\S+$/.test(e)) { toast.error("Enter a valid email"); return; }
    if (emails.includes(e)) { setEmailDraft(""); return; }
    setEmails([...emails, e]); setEmailDraft("");
  };

  const send = async () => {
    if (emails.length === 0 && emailDraft.trim()) addChip();
    const finalList = emails.length ? emails : (/^\S+@\S+\.\S+$/.test(emailDraft.trim().toLowerCase()) ? [emailDraft.trim().toLowerCase()] : []);
    if (finalList.length === 0) { toast.error("Add at least one recipient"); return; }
    setBusy(true);
    try {
      for (const em of finalList) {
        await api.post("/trusted-circle/members", {
          email: em, name: null, preset,
          auto_share: true, trip_type_filter: "any",
          destination_filter: null, notify_on_change: true,
        });
      }
      toast.success(`Invitation sent to ${finalList.length} ${finalList.length === 1 ? "person" : "people"}`);
      onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send invitations");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose} data-testid="trusted-add-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
        {/* Header — Cancel · title · Send (matches TripIt screenshot) */}
        <div className="px-4 pt-3 pb-2 flex items-center justify-between border-b border-slate-100">
          <button onClick={onClose} data-testid="trusted-add-cancel"
            className="w-9 h-9 rounded-full grid place-items-center hover:bg-slate-100">
            <X className="w-5 h-5 text-slate-700" />
          </button>
          <div className="font-serif font-800 text-lg text-slate-900">Add to Inner Circle</div>
          <button onClick={send} disabled={busy}
            data-testid="trusted-add-send"
            className="rounded-full px-4 py-1.5 text-sm font-800 text-blue-700 hover:bg-blue-50 disabled:opacity-40">
            {busy ? "Sending…" : "Send"}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* To: row with chip list + add button */}
          <div className="px-4 py-3 border-b border-slate-100" data-testid="trusted-add-to-row">
            <div className="flex items-start gap-2">
              <div className="text-slate-500 text-base font-500 pt-1.5">To:</div>
              <div className="flex-1 min-w-0 flex flex-wrap gap-1.5 items-center">
                {emails.map((em) => (
                  <span key={em} data-testid={`trusted-chip-${em}`}
                    className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-800 border border-blue-200 px-2.5 py-1 text-sm">
                    {em}
                    <button onClick={() => setEmails(emails.filter((x) => x !== em))}
                      className="hover:text-blue-900" aria-label={`Remove ${em}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                <input value={emailDraft}
                  onChange={(e) => setEmailDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addChip(); } }}
                  data-testid="trusted-add-email"
                  type="email"
                  placeholder={emails.length === 0 ? "jane@example.com" : ""}
                  className="flex-1 min-w-[140px] bg-transparent text-slate-900 text-base py-1 outline-none border-0 focus:ring-0" />
              </div>
              <button onClick={addChip} data-testid="trusted-add-plus"
                className="w-8 h-8 rounded-full border-2 border-blue-500 text-blue-600 grid place-items-center hover:bg-blue-50 shrink-0">
                <span className="text-xl leading-none pb-0.5">+</span>
              </button>
            </div>
          </div>

          {/* Prewritten message */}
          <div className="px-4 py-3 border-b border-slate-100" data-testid="trusted-add-message">
            <div className="text-[13px] text-slate-500 mb-1">Message</div>
            <p className="text-slate-800 text-base leading-relaxed">{messagePreview}</p>
          </div>

          {/* Permissions of Recipients section */}
          <div className="pt-3" data-testid="trusted-add-permissions">
            <div className="px-4 pb-2 text-[11px] font-800 uppercase tracking-widest text-slate-500">Permissions of recipients</div>
            {[
              ["view",   "Can View", "Sees the itinerary only. No costs, no PNRs, no documents."],
              ["helper", "Can Edit", "Can edit shared tasks and itinerary items. Does NOT count as a traveler."],
            ].map(([k, label, desc]) => (
              <button key={k} type="button" onClick={() => setPreset(k)}
                data-testid={`trusted-preset-${k}`}
                className="w-full text-left px-4 py-3.5 border-t border-slate-100 flex items-start gap-3 hover:bg-slate-50">
                <div className="flex-1 min-w-0">
                  <div className="text-base font-500 text-slate-900">{label}</div>
                  <div className="text-[12px] text-slate-500 mt-0.5">{desc}</div>
                </div>
                {preset === k && <span className="text-blue-600 text-lg font-800 mt-0.5">✓</span>}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid, type = "text" }) {
  return (
    <label className="block">
      <div className="text-[11px] font-700 uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <input data-testid={testid} type={type} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-slate-200 bg-white text-slate-800 text-sm py-2 px-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
    </label>
  );
}
